import { MOVEMENT_TYPE, POINT_PLACEHOLDER } from '../constants.js';
import { MOVEMENT_CONTROLS, isAnomalyText } from '../texts.js';
import { addDays, toRome } from '../time.js';
import type { ComputedMovement, DomainSnapshot, IsoDate, IsoDateTime } from '../types.js';
import {
  buildLedger,
  defectiveAt,
  hubAt,
  pointStockAt,
  type Ledger,
  type MovementEntry,
} from './ledger.js';

/** Pickups from TPC from this Rome time on belong to the next operating day. */
const PICKUP_NEXT_DAY_FROM_MIN = 12 * 60;
/** Other movements before this Rome time belong to the previous operating day. */
const NIGHT_PREVIOUS_DAY_UNTIL_MIN = 4 * 60;

/**
 * Operating day of a movement (docs/05 §4, workbook column N):
 * forced day if given; type 1 at Rome time ≥ 12:00 → calendar day + 1;
 * other types at Rome time < 04:00 → calendar day − 1; otherwise the Rome calendar day.
 */
export function operatingDayFor(
  typeId: number,
  occurredAtIso: IsoDateTime,
  forced?: IsoDate | null,
): IsoDate {
  if (forced) return forced;
  const { date, minutes } = toRome(occurredAtIso);
  if (typeId === MOVEMENT_TYPE.PICKUP_FROM_TPC) {
    return minutes >= PICKUP_NEXT_DAY_FROM_MIN ? addDays(date, 1) : date;
  }
  return minutes < NIGHT_PREVIOUS_DAY_UNTIL_MIN ? addDays(date, -1) : date;
}

/** Document number `PREFIX-NNN` (RIT-001…); `n` is the per-type sequence number. */
export function formatDocNumber(prefix: string, n: number): string {
  return `${prefix}-${String(n).padStart(3, '0')}`;
}

/** Types whose last movement of the day must leave the point empty (control 7). */
const RETURN_TYPE_IDS: readonly number[] = [
  MOVEMENT_TYPE.RETURN_FROM_POINT,
  MOVEMENT_TYPE.DEFECTIVE_RETURN_FROM_POINT,
];

/**
 * Whether `e` is the last return (type 3 or 8, not cancelled) of its point on its operating day:
 * workbook `$B >= MAX(MAXIFS(tipo 3), MAXIFS(tipo 8))` on the same point and operating day.
 */
function isLastReturnOfDay(l: Ledger, e: MovementEntry): boolean {
  const m = e.movement;
  let last = Number.NEGATIVE_INFINITY;
  for (const other of l.movements) {
    const o = other.movement;
    if (
      !o.cancelled &&
      RETURN_TYPE_IDS.includes(o.typeId) &&
      o.pointId === m.pointId &&
      other.day === e.day &&
      other.ms > last
    ) {
      last = other.ms;
    }
  }
  return e.ms >= last;
}

/** Control text of a non-cancelled movement (docs/05 §4, first matching rule; workbook column Y). */
function movementControl(
  l: Ledger,
  e: MovementEntry,
  hubAfter: number,
  pointAfter: number | null,
  defectiveAfter: number,
): string {
  const m = e.movement;
  if (Number.isNaN(e.ms)) return MOVEMENT_CONTROLS.missingDateTime;
  if (e.type?.requiresPoint && m.pointId == null) return MOVEMENT_CONTROLS.missingPoint;
  if (!Number.isFinite(m.quantity)) return MOVEMENT_CONTROLS.missingQuantity;
  if (hubAfter < 0) return MOVEMENT_CONTROLS.hubNegative;
  if (pointAfter != null && pointAfter < 0) return MOVEMENT_CONTROLS.pointNegative;
  if (defectiveAfter < 0) return MOVEMENT_CONTROLS.defectiveNegative;
  if (RETURN_TYPE_IDS.includes(m.typeId) && (pointAfter ?? 0) !== 0 && isLastReturnOfDay(l, e)) {
    return MOVEMENT_CONTROLS.returnMismatch(pointAfter ?? 0);
  }
  if (!m.signed) return MOVEMENT_CONTROLS.notSigned;
  return MOVEMENT_CONTROLS.ok;
}

function fillPoint(label: string, pointName: string): string {
  return label.split(POINT_PLACEHOLDER).join(pointName);
}

/** Computed movements of a ledger (shared with the dashboard). */
export function computeMovementsFromLedger(l: Ledger): ComputedMovement[] {
  const pointNames = new Map(l.snap.points.map((p) => [p.id, p.name]));
  return l.movements.map((e) => {
    const m = e.movement;
    const pointName = m.pointId == null ? '' : (pointNames.get(m.pointId) ?? '');
    const hubAfter = hubAt(l, e.ms);
    const pointAfter = m.pointId == null ? null : pointStockAt(l, m.pointId, e.ms);
    const defectiveAfter = defectiveAt(l, e.ms);
    const control = m.cancelled
      ? MOVEMENT_CONTROLS.cancelled(m.cancelReason ?? '')
      : movementControl(l, e, hubAfter, pointAfter, defectiveAfter);
    return {
      ...m,
      // blank, as the workbook, when the movement has no valid date
      operatingDay: e.day ?? '',
      fromLabel: fillPoint(e.type?.fromLabel ?? '', pointName),
      toLabel: fillPoint(e.type?.toLabel ?? '', pointName),
      effectiveQuantity: e.quantity,
      deltaHub: e.deltaHub,
      deltaPoint: e.deltaPoint,
      deltaDefective: e.deltaDefective,
      hubAfter,
      pointAfter,
      defectiveAfter,
      control,
      isAnomaly: !m.cancelled && isAnomalyText(control),
    };
  });
}

/**
 * Computed columns of every movement (docs/05 §4): labels, deltas, stocks after the movement
 * (sums over `occurredAt ≤ t`), control text and anomaly flag. Cancelled movements have effect 0
 * and control `Annullato: <motivo>`.
 */
export function computeMovements(snap: DomainSnapshot): ComputedMovement[] {
  return computeMovementsFromLedger(buildLedger(snap));
}
